import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { styled } from "@mui/material/styles";
import {
  Modal,
  IconButton,
  Box,
  Paper,
  Tabs,
  Tab,
  Badge,
  circularProgressClasses,
  CircularProgress,
} from "@mui/material";
import { MessageCircleMore, X } from "lucide-react";
import { ThreadList } from "./ThreadList";
import { getThreads } from "../../../utils/ApiCall";
import { UserContext } from "../../../UserContext";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Media from "./Media";
import ModalImage from "./ModalImage";
import Notes from "./Notes";
import ActivityLog from "./ActivityLog";

import RowAssistant from "./RowAssistant";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const StyledModal = styled(Modal)({
  display: "flex",
  alignItems: "stretch",
  justifyContent: "flex-end",
});

const ModalContent = styled(Paper)(({ theme }) => ({
  position: "relative",
  width: "1000px",
  height: "100vh",
  maxWidth: "100%",
  // overflow: "hidden",
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[5],
  animation: "slideIn 0.3s ease-out",
  "@keyframes slideIn": {
    from: {
      transform: "translateX(100%)",
    },
    to: {
      transform: "translateX(0)",
    },
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: theme.palette.grey[500],
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      style={{ display: value === index ? "block" : "none" }}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <div>{children}</div>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    right: -3,
    top: 13,
    padding: "0 4px",
    border: `2px solid ${theme.palette.background.paper}`,
    backgroundColor: "rgb(59, 130, 246)",
  },
}));

const ThreadListModal = ({ row }) => {
  const { key, setKey } = useContext(UserContext);
  setKey(localStorage?.getItem("jwt"));

  const avatar = localStorage.getItem("avatar");
  const currentUserId = localStorage.getItem("id");
  const currentUserEmail = localStorage.getItem("email");
  const currentUserUsername = localStorage.getItem("username");

  const [, setOpen] = React.useState(false);
  const [threads, setThreads] = React.useState([]);
  const [itemMedia, setItemMedia] = React.useState([]);
  const [notes, setNotes] = React.useState([]);
  const [users, setUsers] = useState([]);
  const [modalImage, setModalImage] = useState(null);
  const [numberOfThreads, setNumberOfThreads] = useState(row?.threads?.count);
  const [valueTab, setValueTab] = useState(0);
  const [loadingThreads, setLoadingThreads] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();
  // let searchParams = new URLSearchParams(location.search);
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  let pathname = window.location.pathname.replace("/", "");
  const initiatorValue = searchParams.get("initiator");
  let updateValue = searchParams.get("update");

  const [update, setUpdate] = useState(updateValue);

  const contenidoRef = useRef(null);
  const imgRefs = useRef({});

  const handleOpen = () => setOpen(true);

  const handleThreads = useCallback(
    async (id) => {
      let header;
      header = {
        Accept: "/",
        Authorization: `Bearer ${key}`,
      };
      window.modalThreadOpen = true;
      const response = await getThreads(pathname, id, header);
      setThreads(response.threads);
      setItemMedia(response.media);
      setNotes(response.notes);
      setLoadingThreads(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key]
  );

  useEffect(() => {
    if (+row.id === +update) {
      handleThreads(+update);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const updateValue = searchParams.get("update");
    if (updateValue) {
      setUpdate(updateValue);
    }
  }, [searchParams]);

  const handleClose = () => {
    window.modalThreadOpen = false;
    setThreads([]);
    if (update !== null) {
      navigate(`/${pathname}`);
    }
  };

  const handleTab = (event, newValue) => {
    event.preventDefault();
    setValueTab(newValue);
  };

  const handleImageClick = (e, file) => {
    e.preventDefault();
    setModalImage(file);
  };

  function FacebookCircularProgress(props) {
    return (
      <Box sx={{ position: "relative" }}>
        {/* Fondo del círculo */}
        <CircularProgress
          variant="determinate"
          sx={(theme) => ({
            color: theme.palette.grey[200],
            ...(theme.palette.mode === "dark" && {
              color: theme.palette.grey[800],
            }),
          })}
          size={40}
          thickness={4}
          value={100}
          {...props}
        />

        <CircularProgress
          variant="indeterminate"
          disableShrink
          sx={(theme) => ({
            color: "rgb(59, 130, 246)",
            animationDuration: "550ms",
            position: "absolute",
            left: 0,
            [`& .${circularProgressClasses.circle}`]: {
              strokeLinecap: "round",
            },
            ...(theme.palette.mode === "dark" && {
              color: "#308fe8",
            }),
          })}
          size={40}
          thickness={4}
          {...props}
        />
      </Box>
    );
  }

  return (
    <>
      <div>
        <IconButton
          onClick={() => {
            handleOpen();
            handleThreads(row.id);
          }}
        >
          <StyledBadge badgeContent={numberOfThreads} color="info" max={99}>
            <MessageCircleMore
              style={{
                color: "var(--grayTwo)",
                cursor: "pointer",
              }}
            />
          </StyledBadge>
        </IconButton>

        <StyledModal
          open={window.modalThreadOpen}
          onClose={handleClose}
          aria-labelledby="thread-modal-title"
        >
          <ModalContent>
            <h1
              style={{
                fontWeight: "medium",
                fontSize: "22px",
                marginBottom: "10px",
              }}
            >
              {pathname.startsWith("master") ? (
                <>
                  <span style={{ fontWeight: "bold" }}>{row.offerID}</span> -{" "}
                  <span>{row.offerTitle}</span>
                </>
              ) : (
                row.offerID
              )}
            </h1>

            <Tabs value={valueTab} onChange={handleTab}>
              <Tab label="Actualizaciones" {...a11yProps(0)} />
              <Tab
                label={
                  [
                    "it-request",
                    "ready-leads-supply",
                    "ready-leads-demand",
                  ].includes(pathname)
                    ? "Archivos"
                    : pathname === "master-branding"
                    ? "INFO CLIENTE/MATERIALES"
                    : pathname === "crm"
                    ? "Propuestas Enviadas"
                    : "Reporte Invalidaciones"
                }
                {...a11yProps(1)}
              />
              {pathname === "crm" ? null : (
                <Tab
                  label={
                    [
                      "it-request",
                      "ready-leads-supply",
                      "ready-leads-demand",
                    ].includes(pathname)
                      ? "Notas"
                      : pathname === "master-branding"
                      ? "COSTOS/REPORTES"
                      : "CSM"
                  }
                  {...a11yProps(2)}
                />
              )}

              <Tab label="Activity Log" {...a11yProps(3)} />
              <Tab label="KALA-IA" {...a11yProps(4)} />
            </Tabs>

            <CloseButton onClick={handleClose} aria-label="close">
              <X />
            </CloseButton>

            <Box
              sx={{
                height: "calc(100vh - 100px)",
                overflowY: "scroll",
                "&::-webkit-scrollbar": {
                  width: "8px",
                  height: "50px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#888",
                  borderRadius: "4px",
                  "&:hover": {
                    background: "#555",
                  },
                },
              }}
            >
              <TabPanel value={valueTab} index={0}>
                <ThreadList
                  threads={threads}
                  setThreads={setThreads}
                  backendUrl={backendUrl}
                  currentUserId={currentUserId}
                  currentUserEmail={currentUserEmail}
                  currentUserUsername={currentUserUsername}
                  pathname={pathname}
                  threadKey={key}
                  row={row}
                  avatar={avatar}
                  users={users}
                  setUsers={setUsers}
                  initiatorValue={initiatorValue}
                  numberOfThreads={numberOfThreads}
                  setNumberOfThreads={setNumberOfThreads}
                  loadingThreads={loadingThreads}
                />
              </TabPanel>

              <TabPanel value={valueTab} index={1}>
                <Media
                  id={row.id}
                  media={itemMedia}
                  backendUrl={backendUrl}
                  handleImageClick={handleImageClick}
                  contenidoRef={contenidoRef}
                  imgRefs={imgRefs}
                  setItemMedia={(media) => setItemMedia(media)}
                  itemMedia={itemMedia}
                />
              </TabPanel>

              <TabPanel value={valueTab} index={2}>
                <Notes
                  id={row.id}
                  notes={notes}
                  setNotes={(note) => setNotes(note)}
                  backendUrl={backendUrl}
                  imgRefs={imgRefs}
                  contenidoRef={contenidoRef}
                  handleimageclick={handleImageClick}
                  row={row}
                />
              </TabPanel>

              <TabPanel value={valueTab} index={3}>
                <ActivityLog row={row} />
              </TabPanel>
              <TabPanel value={valueTab} index={4}>
                <RowAssistant row={row} pathname={pathname} />
              </TabPanel>
            </Box>

            {loadingThreads && (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  top: "0",
                  left: "0",
                  position: "absolute",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: "-1",
                }}
              >
                <FacebookCircularProgress />
              </Box>
            )}
          </ModalContent>
        </StyledModal>

        {modalImage && (
          <ModalImage
            setModalImage={(image) => setModalImage(image)}
            modalImage={modalImage}
            row={row}
            backendUrl={backendUrl}
            pathname={pathname}
          />
        )}
      </div>
    </>
  );
};

export default ThreadListModal;
